import dayjs, { Dayjs } from "dayjs";
import usePrevious from "lib/use-previous";
import { nanoid } from "nanoid";
import { useRouter } from "next/router";
import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useMonitoringFile } from "./MonitoringFileContext";
import { useActiveOrganization } from "./ActiveOrganizationProvider";
import { VersionStatus } from "@/types/app/version";
import { CategoricalFilter, TextFilter } from "@components/llama/Tables/utils";
import { SearchType } from "@components/llama/Tables/TableSearchTypeDropdown";
import {
  ContainsButLooksLikeIsOperator,
  ContainsOperator,
  Filter,
  isDateFilter,
  isKeyValueFilter,
  IsOperator,
} from "@/lib/filtering/filters";
import _ from "lodash";
import { useQueryState } from "nuqs";
import { parseAsArrayOf } from "nuqs";

export interface ProjectPage {
  Icon: (props: React.SVGProps<SVGSVGElement>) => JSX.Element;
  slug: string;
}
export type CategoricalTableFilterState = Record<string, Omit<CategoricalFilter, "kind">>;
export type TextTableFilterState = Record<string, Omit<TextFilter, "kind">>;

interface TableFilterState {
  search: string;
  setSearch: React.Dispatch<React.SetStateAction<string>>;
  searchType: SearchType;
  metadataKeySearch: { key: string; value: string }[];
  inputKeySearch: { key: string; value: string }[];
  setSearchType: React.Dispatch<React.SetStateAction<SearchType>>;
  startDate: Dayjs | null;
  endDate: Dayjs | null;
  textFilters: TextTableFilterState;
  setTextFilters: React.Dispatch<React.SetStateAction<TextTableFilterState>>;
  filtersActive: boolean;
  filters: Filter[];
  setFilters: React.Dispatch<React.SetStateAction<Filter[]>>;

  // Trigger rerenders onGridReady, as child components may make use of colDefs that are not available on first render.
  gridReadySignal: string | null;
  onGridReady: () => void;

  // Whether we're fetching data for the table.
  // (State is held in this context, but loading state has to be updated in the component that fetches the data.)
  loading: boolean;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

interface LlamaPageContextInterface {
  datapointsTableRowCount: number | null;
  setDatapointsTableRowCount: React.Dispatch<React.SetStateAction<number | null>>;
}

const LlamaPageContext = createContext<(LlamaPageContextInterface & TableFilterState) | undefined>(undefined);

export const VERSION_ID_HEADER_NAME = "version_id";
export const SOURCE_HEADER_NAME = "source";
export const METADATA_KEY_TARGET = "metadata_key";
export const METADATA_KEY_TARGET_LABEL = "Metadata by key";
export const INPUT_KEY_TARGET = "input_key";
export const INPUT_KEY_TARGET_LABEL = "Input by key";
export const START_DATE_TARGET = "startDate";
export const START_DATE_TARGET_LABEL = "Start date";
export const END_DATE_TARGET = "endDate";
export const END_DATE_TARGET_LABEL = "End date";
/**
 * Holds state for the active tab and a filter state for the datapoints table.
 * TODO: Consider moving datapoints table filter state to its own context. Here as a convenience for now.
 * (The datapoints table filter state is in a context to allow other tabs to set the filter state when navigating to the table.)
 */
export const useLlamaPageContext = () => {
  const context = useContext(LlamaPageContext);
  if (context === undefined) {
    throw new Error("LlamaPageContext must be used within a LlamaPageContextProvider");
  }
  return context;
};

const parseUrlFilters = (urlString: string): Filter => {
  // Check if this is a key-value filter in the format metadata.key:value
  const keyValueMatch = urlString.match(/^(metadata|inputs)\.([^~]+)~(.+)$/);
  const dateMatch = urlString.match(/^(startDate|endDate):(\d+)$/);

  if (keyValueMatch) {
    const [_, target, key, value] = keyValueMatch;
    const targetKey = target === "metadata" ? METADATA_KEY_TARGET : INPUT_KEY_TARGET;

    return {
      target: {
        target: targetKey,
        label: targetKey === METADATA_KEY_TARGET ? METADATA_KEY_TARGET_LABEL : INPUT_KEY_TARGET_LABEL,
        errorState: false,
        entity: targetKey === INPUT_KEY_TARGET ? "inputs" : undefined,
        type: "key_value",
      },
      type: "key_value",
      operator: ContainsOperator,
      value: { key, value },
    };
  }

  // Handle date filters
  if (dateMatch) {
    const [_, target, timestamp] = dateMatch;
    const date = dayjs.unix(parseInt(timestamp, 10));
    return {
      target: {
        target,
        label: target === START_DATE_TARGET ? START_DATE_TARGET_LABEL : END_DATE_TARGET_LABEL,
        errorState: false,
        type: "date",
      },
      type: "date",
      operator: IsOperator,
      value: date,
    };
  }

  // Handle other filters
  const [target, values] = urlString.split(":");
  const value = values.split(";");

  return {
    target: {
      target: target,
      label: target,
      errorState: false,
      type: "categorical_multi", // we only support categorical_multi filters in the URL for now
    },
    type: "categorical_multi", // we only support categorical_multi filters in the URL for now
    operator: ContainsButLooksLikeIsOperator,
    value: value,
  };
};

const serializeFilters = (filter: Filter) => {
  // TODO: unify with addSortAndFilterURLSearchParams function
  if (filter.value === null || filter.value?.value === null) {
    return "";
  }

  // Handle key-value filters
  if (isKeyValueFilter(filter)) {
    const { key, value } = filter.value;
    if (key && value) {
      // Convert internal target names to user-facing names
      const targetName = filter.target.target === METADATA_KEY_TARGET ? "metadata" : "inputs";
      return `${targetName}.${key}~${value}`;
    }
  }

  if (isDateFilter(filter)) {
    const timestamp = filter.value.unix();
    return `${filter.target.target}:${timestamp}`;
  }

  // Handle all other filters
  const value = Array.isArray(filter.value) ? filter.value.join(";") : filter.value;
  return `${filter.target.target}:${value}`;
};

const createFilters = () => {
  return {
    parse: (value: string) => parseUrlFilters(value),
    serialize: serializeFilters,
  };
};

export const LlamaPageContextProvider = ({ children }: React.PropsWithChildren<{}>) => {
  const router = useRouter();
  const { slug } = useActiveOrganization();
  const [search, setSearch] = useState<string>("");
  const [metadataKeySearch, setMetadataKeySearch] = useState<{ key: string; value: string }[]>([]);
  const [inputKeySearch, setInputKeySearch] = useState<{ key: string; value: string }[]>([]);
  const [searchType, setSearchType] = useState<SearchType>("inputs");
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);
  const [textFilters, setTextFilters] = useState<TextTableFilterState>({});
  const [datapointsTableRowCount, setDatapointsTableRowCount] = useState<number | null>(null);
  const [gridReadySignal, setGridReadySignal] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [filters, setFilters] = useQueryState<Filter[]>("filters", parseAsArrayOf(createFilters()).withDefault([]));

  const filtersActive = !(
    search.length === 0 &&
    startDate === null &&
    endDate === null &&
    (filters.length === 0 || filters.every((filter) => filter.value === null)) &&
    !Object.values(textFilters).some(({ text }) => text && text.length > 0)
  );

  const { file } = useMonitoringFile();

  useEffect(() => {
    // Convert unified Filter objects to legacy filter formats
    const newMetadataKeySearch: { key: string; value: string }[] = [];
    const newInputKeySearch: { key: string; value: string }[] = [];
    let newStartDate = null;
    let newEndDate = null;
    let showOnlyCommittedVersions = false;

    filters.forEach((filter) => {
      // Skip inactive filters
      if (filter.value === null || filter.value?.value === null) return;

      const { target, value } = filter;

      // Handle metadata key filters
      if (target.target === METADATA_KEY_TARGET) {
        const key = value.key as string;
        newMetadataKeySearch.push({ key, value: value.value });
        return;
      }

      // Handle input key filters
      if (target.target === INPUT_KEY_TARGET) {
        const key = value.key as string;
        newInputKeySearch.push({ key, value: value.value });
        return;
      }

      if (target.target === START_DATE_TARGET) {
        newStartDate = value;
        return;
      }

      if (target.target === END_DATE_TARGET) {
        newEndDate = value;
      }
    });

    // Update state only if values have changed
    setMetadataKeySearch((prev) => (_.isEqual(prev, newMetadataKeySearch) ? prev : newMetadataKeySearch));
    setInputKeySearch((prev) => (_.isEqual(prev, newInputKeySearch) ? prev : newInputKeySearch));
    if (newStartDate !== startDate) {
      setStartDate(newStartDate);
    }
    if (newEndDate !== endDate) {
      setEndDate(newEndDate);
    }
  }, [filters]);

  const previousProjectId = usePrevious(file?.id);
  useEffect(() => {
    // TODO: Can we extract the file from the URL and not wait on the hook above?
    if (previousProjectId !== undefined && file?.id !== previousProjectId) {
      // Reset filters when changing projects
      setFilters([]);
      setSearch("");
      setTextFilters({});
    }
  }, [previousProjectId, file?.id, router, slug]);

  const onGridReady = useCallback(() => {
    setGridReadySignal(nanoid());
  }, []);

  return (
    <LlamaPageContext.Provider
      value={{
        search,
        setSearch,
        searchType,
        setSearchType,
        startDate,
        endDate,
        textFilters,
        setTextFilters,
        datapointsTableRowCount,
        setDatapointsTableRowCount,
        gridReadySignal,
        onGridReady,
        filtersActive,
        metadataKeySearch,
        inputKeySearch,
        loading,
        setLoading,
        filters,
        setFilters,
      }}
    >
      {children}
    </LlamaPageContext.Provider>
  );
};
